import {
  ArrowRightOutline18Icon,
  ChapterFill18Icon,
} from "@vygruppen/spor-icon-react";
import { PlanInternalMessage } from "features/CenterContent/RoleContent/EventHandling/components/EventChangesDialog/types";
import { TabIds } from "features/CenterContent/RoleContent/useTabsForRole";
import {
  eventIsInfrastructureEvent,
  eventIsTrainEvent,
} from "features/History/utils/utils";
import { FC } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { HStack, Stack, Text, TextLink, tokens } from "shared/components/Spor";
import { CombinedHistoryResponse } from "shared/types/operationalInformation";
import { LANDING } from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import styled from "styled-components";

const NoVisibleChangesWrapper = styled.div`
  display: none;

  &&&:only-child {
    display: flex;
    flex-flow: column nowrap;
    gap: ${tokens.default.size.spacing["2"]};
  }
`;

type InfraEventChangesProps = {
  currentInformation: CombinedHistoryResponse;
  previousInformation: CombinedHistoryResponse;
  currentInternalMessages: PlanInternalMessage[];
  previousInternalMessages: PlanInternalMessage[];
};

function getTitle(event: CombinedHistoryResponse): string {
  if (eventIsInfrastructureEvent(event)) return event.stretchName;
  if (eventIsTrainEvent(event)) return event.trainIdentifier.identifier;

  return event.type.label ?? event.type.type;
}

export const EventChangesBody: FC<InfraEventChangesProps> = ({
  currentInformation,
  previousInformation,
  currentInternalMessages,
  previousInternalMessages,
}) => {
  const navigate = useNavigate();
  const { role } = useDropsRole();

  const currentTitle = getTitle(currentInformation);
  const previousTitle = getTitle(previousInformation);

  const currentMessage = currentInformation.distributions.find(
    (d) => d.contextName === "DROPS",
  )?.text.message;
  const previousMessage = previousInformation.distributions.find(
    (d) => d.contextName === "DROPS",
  )?.text.message;

  const currentInternalMessage = currentInternalMessages.at(0);
  const previousInternalMessage = previousInternalMessages.at(0);

  const currentActionCard = eventIsInfrastructureEvent(currentInformation)
    ? currentInformation.actionCard?.type.replace("_", " ")
    : undefined;

  const previousActionCard = eventIsInfrastructureEvent(previousInformation)
    ? previousInformation.actionCard?.type.replace("_", " ")
    : undefined;

  return (
    <>
      {currentTitle !== previousTitle && (
        <Stack gap="0.5">
          <Text fontWeight={700} color="text.disabled">
            {previousTitle}
          </Text>
          <Text fontWeight={700}>{currentTitle}</Text>
        </Stack>
      )}

      {currentMessage !== previousMessage && (
        <Stack gap="0.5">
          <Text variant="xs" color="text.disabled">
            {previousMessage}
          </Text>
          <Text variant="xs">{currentMessage}</Text>
        </Stack>
      )}

      {currentInternalMessages.length !== previousInternalMessages.length && (
        <Stack gap="0.5">
          <Text variant="2xs">Internmelding</Text>
          <Text variant="xs" color="text.disabled">
            {previousInternalMessage?.messages.NOB?.message ?? "–"}
          </Text>
          <Text variant="xs">
            {currentInternalMessage?.messages.NOB?.message ?? "–"}
          </Text>
        </Stack>
      )}

      {currentActionCard !== previousActionCard && (
        <Stack gap="0.5">
          <HStack gap="1" color="text.disabled">
            <ChapterFill18Icon />
            <Text variant="sm" fontWeight="700">
              Aksjonskort {previousActionCard ?? "–"}
            </Text>
          </HStack>

          <HStack gap="1">
            <ChapterFill18Icon />
            <Text variant="sm" fontWeight="700">
              Aksjonskort {currentActionCard ?? "–"}
            </Text>
          </HStack>
        </Stack>
      )}

      <NoVisibleChangesWrapper>
        <Text variant="xs">
          En endring er registrert på hendelsen, men den påvirker ikke
          informasjonen i Tiltaksoversikt.
        </Text>
        <Text variant="xs">
          Gå til hendelsesdetaljene i Oversikt dersom du ønsker å se all
          informasjon.
        </Text>

        <TextLink
          onClick={() => {
            navigate({
              pathname: generatePath(LANDING, {
                roleIdParam: role,
                tabIdParam: TabIds.HISTORY,
              }),
              search: new URLSearchParams({
                historyEventUuid: currentInformation.uuid,
              }).toString(),
            });
          }}
          size="xs"
        >
          Se detaljer{" "}
          <ArrowRightOutline18Icon
            width={24}
            height={24}
            display="inline-block"
          />
        </TextLink>
      </NoVisibleChangesWrapper>
    </>
  );
};
